/**
 * Rank lookups for XP celebrations. Indices clamp into 1..10 so a bad payload
 * degrades to a valid rank instead of crashing a celebration mid-ceremony.
 */

import {
  LEVELS_PER_RANK,
  RANK_ACCENT_COLORS,
  RANKS,
  TOP_RANK_INDEX,
  type RankDef,
} from '../constants/ranks.constants';
import { RANK_COIN_ENTRY } from '../constants/rankThresholds.constants';

export function getRank(index: number): RankDef {
  const clamped = Math.min(TOP_RANK_INDEX, Math.max(1, Math.round(index)));
  return RANKS[clamped - 1];
}

/** Accent colour for a rank, clamped like getRank so a bad index still returns a usable colour. */
export function getRankAccentColor(index: number): string {
  const clamped = Math.min(TOP_RANK_INDEX, Math.max(1, Math.round(index)));
  return RANK_ACCENT_COLORS[clamped - 1];
}

export function getRankForLevel(level: number): RankDef {
  return getRank(Math.ceil(Math.max(1, level) / LEVELS_PER_RANK));
}

/**
 * Coin-based rank (specs/coin-based-ranks). Returns the highest rank whose peak-coin entry
 * threshold `peakCoins` meets or exceeds. Non-finite/negative → Rookie. Mirrors the server
 * `getRankForPeakCoins` in `server/src/helpers/rankThresholds.helper.js`.
 */
export function getRankForPeakCoins(peakCoins: number): RankDef {
  const peak = Number.isFinite(peakCoins) ? Math.floor(peakCoins) : 0;
  let index = 1;
  while (index < TOP_RANK_INDEX && peak >= RANK_COIN_ENTRY[index]) {
    index += 1;
  }
  return getRank(index);
}

/**
 * Progress toward the next rank as a 0..1 ratio of peak coins across the current rank's band,
 * plus the coins remaining. At the top rank returns `{ progress: 1, coinsToNext: 0, nextEntry: null }`.
 * Drives the Rank Ladder "next rank" bar (specs/coin-based-ranks Q-4).
 */
export function getPeakCoinRankProgress(peakCoins: number): {
  progress: number;
  coinsToNext: number;
  nextEntry: number | null;
} {
  const peak = Number.isFinite(peakCoins) ? Math.max(0, Math.floor(peakCoins)) : 0;
  const rank = getRankForPeakCoins(peak);
  if (rank.index >= TOP_RANK_INDEX) {
    return { progress: 1, coinsToNext: 0, nextEntry: null };
  }
  const bandStart = RANK_COIN_ENTRY[rank.index - 1];
  const nextEntry = RANK_COIN_ENTRY[rank.index];
  const span = nextEntry - bandStart;
  const into = Math.min(span, Math.max(0, peak - bandStart));
  return {
    progress: span > 0 ? into / span : 0,
    coinsToNext: Math.max(0, nextEntry - peak),
    nextEntry,
  };
}

/** Name of the rank after `rank`, or null at the top rank (Icon). */
export function getNextRankName(rank: RankDef): string | null {
  return rank.index >= TOP_RANK_INDEX ? null : RANKS[rank.index].name;
}

/** Progress-bar fill ratio; 0 when the target is missing/invalid (no NaN). */
export function getXpProgress(xpCurrent: number, xpTarget: number): number {
  if (!Number.isFinite(xpCurrent) || !Number.isFinite(xpTarget) || xpTarget <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, xpCurrent / xpTarget));
}
